import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';
import { Card, CardBody } from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import TimeOfUsable from 'src/components/TradeRule/TimeOfUse/TimeOfUsable';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import getNodesFromEdges from 'src/util/graphql';
import logger from 'src/util/logger';
import UUID from 'src/components/UUID';

/**
 * AdminVolumeChargeList component displays a paginated list of volume charges
 * in the admin interface.
 * @param {object} props - Component props
 * @param {object} props.relay - Relay pagination container props
 * @param {object} props.viewer - Viewer object containing volumeCharges data
 * @param {object} props.viewer.volumeCharges - Paginated volumeCharges connection
 * @returns {React.ReactElement} AdminVolumeChargeList component
 */
function AdminVolumeChargeList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { volumeCharges } = viewer;
  const volumeChargesNodes = getNodesFromEdges(volumeCharges);

  const intl = useIntl();

  // Page title and meta information
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.page.title',
    defaultMessage: 'Enosi Admin - Volume Charges',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbVolumeCharges = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.breadcrumbs.volume_charges.label',
    defaultMessage: 'Volume Charges',
  });

  // Table headers
  const tableHeaderTitle = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.title.label',
    defaultMessage: 'Title',
  });
  const tableHeaderIdentifier = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.identifier.label',
    defaultMessage: 'Identifier',
  });
  const tableHeaderId = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.id.label',
    defaultMessage: 'ID',
  });
  const tableHeaderExternalIdentifierTitle = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.external_identifier.title',
    defaultMessage: 'External Identifier',
  });
  const tableHeaderExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.external_identifier.label',
    defaultMessage: 'Ext ID',
  });
  const tableHeaderRate = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.rate.label',
    defaultMessage: 'Rate',
  });
  const tableHeaderTariff = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.table.head.tariff.label',
    defaultMessage: 'Tariff',
  });

  // Unit abbreviation

  // Load more button text
  const loadMoreStateLoadMore = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const loadMoreStateNoMore = intl.formatMessage({
    id: 'admin.admin_volume_charge.admin_volume_charge_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more volume charges to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbVolumeCharges },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbVolumeCharges}</h1>
      </div>
      {volumeChargesNodes.length === 0 && (
      <Card className="mb-1">
        <CardBody>
          {intl.formatMessage({
            id: 'admin.admin_volume_charge.admin_volume_charge_list.no_volume_charges.message',
            defaultMessage: 'Volume charges not configured',
          })}
        </CardBody>
      </Card>
      )}
      {volumeChargesNodes.length > 0 && (
        <div className="mt-4 mb-4 card">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{tableHeaderTitle}</th>
                  <th scope="col">{tableHeaderIdentifier}</th>
                  <th scope="col">{tableHeaderId}</th>
                  <th scope="col">
                    <abbr title={tableHeaderExternalIdentifierTitle}>
                      {tableHeaderExternalIdentifier}
                    </abbr>
                  </th>
                  <th scope="col">{tableHeaderRate}</th>
                  <th scope="col">{tableHeaderTariff}</th>
                </tr>
              </thead>
              <tbody>
                {
                volumeCharges && volumeCharges.edges && volumeCharges.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    id, title, identifier, externalIdentifier, tariff,
                  } = node;

                  return (
                    <tr key={`admin-volume-charge-${id}`}>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/volume-charges/${id}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={id} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        <ul className="ps-2">
                          <TimeOfUsable timeOfUsable={node} showConditions={false} valueField="rate" />
                        </ul>
                      </td>
                      <td>
                        {tariff && (
                          <>
                            <Link to={`/admin/tariffs/${tariff.id}`}>
                              {tariff.title}
                            </Link>
                            <br />
                            <BadgeActive
                              start={tariff.active.start}
                              finish={tariff.active.finish}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
              </tbody>
            </table>
          </div>
          <div className="card-footer">
            <LoadMoreButton
              copyLoadMore={loadMoreStateLoadMore}
              copyNoMore={loadMoreStateNoMore}
              onClick={loadMore}
              hasNext={relay.hasMore()}
            />
          </div>
        </div>
      )}
    </>
  );
}

AdminVolumeChargeList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminVolumeChargeList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminVolumeChargeList,
  {
    viewer: graphql`
      fragment AdminVolumeChargeList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        volumeCharges(
          first: $count
          after: $cursor
        ) @connection(key: "AdminVolumeChargeList_volumeCharges") {
          edges {
            cursor
            node {
              id
              externalIdentifier
              identifier
              title
              rate
              tariff {
                id
                title
                active { start finish }
              }
              monthsOfYear
              daysOfWeek
              timesOfDay {
                start  { hours minutes seconds nanos }
                finish { hours minutes seconds nanos }
              }
              ignorePublicHolidays
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.volumeCharges;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminVolumeChargeList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminVolumeChargeList_viewer
      }
    }
  `,
  },
);
